"use client";

import { useCallback, useMemo, useState } from "react";
import { ExchangeMissingTradeHistoryPermissionError, KATANA_MAINNET_CHAIN_ID } from "@cede/utils";
import { ApiKeyInput } from "../ApiKeyInput";
import { DialogHeader } from "../DialogHeader";
import { FullPageTutorials } from "@/components/features/onboarding/Tutorials/FullPageTutorials";
import { Button } from "@/components/ui/button";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useDevice } from "@/hooks";
import { useSupportedExchanges } from "@/hooks/core/useSupportedExchanges";
import { cn } from "@/lib/utils";
import { ExchangeAccountFormData, useCreateAccountStore } from "@/store/accountStore";
import { Eye, EyeOff } from "lucide-react";
import { useChainId, useSwitchChain } from "wagmi";

interface ApiKeysStepProps {
  title: string;
  subtitle: string;
  onNext: (formData: ExchangeAccountFormData) => void;
  onBack: () => void;
  isLoading: boolean;
  showBackButton: boolean;
}

export const ApiKeysStep = ({ title, subtitle, onNext, onBack, isLoading, showBackButton }: ApiKeysStepProps) => {
  const { isMobile } = useDevice();
  const { supportedExchangesById, isSupportedExchangesLoading } = useSupportedExchanges();
  const currentChainId = useChainId();
  const { switchChainAsync, isPending: isSwitchingChain } = useSwitchChain();
  const { updateFormData, formData, error, setError } = useCreateAccountStore();
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [uid, setUid] = useState("");
  const [password, setPassword] = useState("");
  const [showInputs, setShowInputs] = useState(true);

  const exchangeInfo = useMemo(
    () => supportedExchangesById[formData.selectedCex],
    [formData.selectedCex, supportedExchangesById],
  );

  const isKatana = formData.selectedCex === "katana";
  const uidLabel = formData.selectedCex === "bitmart" ? "Memo" : "User ID";

  const handleSubmit = useCallback(async () => {
    if (isKatana && currentChainId !== KATANA_MAINNET_CHAIN_ID) {
      await switchChainAsync({ chainId: KATANA_MAINNET_CHAIN_ID });
    }
    const newFormData = { ...formData, apiKey, secretKey, uid, password };
    updateFormData(newFormData);
    setError(null);
    onNext(newFormData);
  }, [
    apiKey,
    currentChainId,
    formData,
    isKatana,
    onNext,
    password,
    secretKey,
    setError,
    switchChainAsync,
    uid,
    updateFormData,
  ]);

  const toggleInputsVisibility = () => {
    setShowInputs(!showInputs);
  };

  const canSubmit =
    !!apiKey && !!secretKey && (!exchangeInfo?.requiresUid || !!uid) && (!exchangeInfo?.requiresPassword || !!password);

  const renderCredentialInputs = () => (
    <div className="flex flex-col gap-3 pb-2">
      <ApiKeyInput error={!!error} label="Public API Key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
      <ApiKeyInput
        error={!!error}
        label="Private API Key"
        value={secretKey}
        onChange={(e) => setSecretKey(e.target.value)}
      />
      {exchangeInfo?.requiresUid && (
        <ApiKeyInput error={!!error} label={uidLabel} value={uid} onChange={(e) => setUid(e.target.value)} />
      )}
      {exchangeInfo?.requiresPassword && (
        <ApiKeyInput
          error={!!error}
          type="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      )}
      {error instanceof ExchangeMissingTradeHistoryPermissionError && (
        <p className="text-sm text-cede-error">{error.message}</p>
      )}
    </div>
  );

  const submitLabel = isSwitchingChain
    ? "Switching network..."
    : isKatana
      ? isLoading
        ? "Awaiting Signature..."
        : "Sign to Connect"
      : "Authorize access";
  const isSubmitBusy = isLoading || isSwitchingChain;

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      {isMobile ? (
        <div className="flex flex-col flex-1 min-h-0 w-full gap-3">
          <DialogHeader title={title} showBackButton={showBackButton} centerTitle onBack={onBack} />

          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-visible" style={{ flexBasis: 0 }}>
            {!exchangeInfo || isSupportedExchangesLoading ? (
              <div className="flex flex-col gap-3 px-2">
                <SkeletonElement className="h-14 w-full" />
                <SkeletonElement className="h-14 w-full" />
                <SkeletonElement className="h-14 w-full" />
              </div>
            ) : (
              <div className="flex flex-col gap-3 rounded overflow-hidden pb-2">
                <p className="text-base text-center font-semibold text-cede-text-primary px-2">{subtitle}</p>
                <FullPageTutorials cexId={formData.selectedCex} isMobile={true} />
              </div>
            )}
          </div>

          <div className="flex-shrink-0 flex flex-col gap-3 px-2 pb-2 max-h-[60vh] overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-medium text-cede-text-primary">API Keys</p>
                <p className="text-sm text-cede-text-secondary transition-colors hover:text-cede-text-primary">
                  {showInputs ? "Tap to hide input fields" : "Tap to show input fields"}
                </p>
              </div>
              <button
                onClick={toggleInputsVisibility}
                className="p-2 rounded-lg hover:bg-cede-primary/10 transition-colors text-cede-text-secondary hover:text-cede-primary"
              >
                {showInputs ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div
              className={cn(
                "flex flex-col gap-3 transition-all duration-300 origin-top",
                showInputs ? "opacity-100 max-h-[1000px] mt-2" : "opacity-0 max-h-0 overflow-hidden",
              )}
            >
              {renderCredentialInputs()}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitBusy}
              variant="primary"
              size="medium"
              className="w-full"
              loading={isSubmitBusy}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 w-full max-w-[1200px] flex-col gap-6">
          <DialogHeader title={title} showBackButton={showBackButton} onBack={onBack} />

          <div className="flex flex-1 min-h-0 flex-row gap-6">
            <div className="flex flex-1 min-h-0 min-w-0 max-w-[520px] flex-col rounded-2xl border border-cede-border-container bg-cede-bg-component/70 p-6 shadow-sm">
              <div className="flex flex-shrink-0 items-center justify-between">
                <div>
                  <p className="text-base font-semibold text-cede-text-primary">API Keys</p>
                  {subtitle && <p className="text-sm text-cede-text-secondary">{subtitle}</p>}
                </div>
              </div>

              <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
                {!isKatana && (!exchangeInfo || isSupportedExchangesLoading) && (
                  <div className="flex flex-1 flex-col gap-4">
                    <SkeletonElement className="h-14 w-full" />
                    <SkeletonElement className="h-14 w-full" />
                    <SkeletonElement className="h-14 w-full" />
                  </div>
                )}

                {(isKatana || (exchangeInfo && !isSupportedExchangesLoading)) && (
                  <div className="flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-visible">
                    {renderCredentialInputs()}
                  </div>
                )}
              </div>

              <div className="mt-6 flex-shrink-0 border-t border-cede-border-container pt-4">
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || isSubmitBusy}
                  variant="primary"
                  size="medium"
                  className="w-full"
                  loading={isSubmitBusy}
                >
                  {submitLabel}
                </Button>
              </div>
            </div>

            <div className="flex min-h-0 min-w-[400px] flex-shrink-0" style={{ width: "calc(100% - 520px - 1.5rem)" }}>
              <FullPageTutorials cexId={formData.selectedCex} isMobile={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
